import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  User,
  Phone,
  FileText,
  Clock,
  Wifi,
  WifiOff,
  Plus,
  X,
  Save,
  Search,
  Loader2,
  AlertCircle,
  CheckCircle,
  Hash,
  RefreshCw,
  Repeat2,
  DollarSign,
  Calendar as CalendarIcon,
  Send,
  QrCode,
  Unlock,
  Wrench,
  Activity,
  Globe,
  AlertTriangle,
  Copy,
  Check
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { Avatar, AvatarFallback } from './ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Calendar } from './ui/calendar';
import { cn, formatDateTime, getInitials } from '../lib/utils';
import { consultarClienteSGP, verificarAcessoSGP, consultarFinanceiroSGP, enviarBoletoSGP, enviarPixSGP, liberarPromessaSGP, getSGPMotivosOS, getSGPTiposOcorrencia, abrirOrdemServicoSGP, getSGPExtratoUso } from '../lib/api';
import { toast } from 'sonner';

export const CustomerPanel = ({
  conversation,
  onUpdateConversation,
  onAddNote,
  promessaDias = 5
}) => {
  const [newNote, setNewNote] = useState('');
  const [isEditingCustomer, setIsEditingCustomer] = useState(false);
  const [customerData, setCustomerData] = useState({
    customer_name: '',
    customer_cpf: '',
    customer_plan: '',
    customer_status: '',
    customer_contract: '',
  });

  // SGP Integration states
  const [sgpLoading, setSgpLoading] = useState(false);
  const [sgpData, setSgpData] = useState(null);
  const [sgpError, setSgpError] = useState(null);
  const [showContractSelector, setShowContractSelector] = useState(false);
  const [lastSearchedCpf, setLastSearchedCpf] = useState('');
  const [lastConversationId, setLastConversationId] = useState('');
  const [statusLoading, setStatusLoading] = useState(false);
  const promptedCpfRef = useRef({});

  // Financial states
  const [financeiroData, setFinanceiroData] = useState(null);
  const [financeiroLoading, setFinanceiroLoading] = useState(false);
  const [sendingBoleto, setSendingBoleto] = useState(false);
  const [sendingPix, setSendingPix] = useState(false);
  const financeiroCacheRef = useRef({});

  // Unlock states
  const [unlockLoading, setUnlockLoading] = useState(false);
  const [showUnlockCalendar, setShowUnlockCalendar] = useState(false);
  const [contratoStatusText, setContratoStatusText] = useState(null);

  // OS (Ordem de Serviço) states
  const [isOSModalOpen, setIsOSModalOpen] = useState(false);
  const [osContrato, setOsContrato] = useState('');
  const [osBairro, setOsBairro] = useState('');
  const [motivosList, setMotivosList] = useState([]);
  const [tiposList, setTiposList] = useState([]);
  const [selectedMotivo, setSelectedMotivo] = useState('');
  const [selectedTipo, setSelectedTipo] = useState('');
  const [osConteudo, setOsConteudo] = useState('');
  const [osObservacao, setOsObservacao] = useState('');
  const [osAgendamento, setOsAgendamento] = useState('');
  const [loadingOSLists, setLoadingOSLists] = useState(false);
  const [submittingOS, setSubmittingOS] = useState(false);

  // Extrato de Tráfego state
  const [extratoLoading, setExtratoLoading] = useState(false);
  const [extratoData, setExtratoData] = useState(null);
  const [activeExtratoTab, setActiveExtratoTab] = useState(0);
  const [copiedIp, setCopiedIp] = useState(false);

  const handleCopyIp = (ipText) => {
    if (!ipText) return;
    navigator.clipboard.writeText(ipText);
    setCopiedIp(true);
    toast.success('IP copiado para a área de transferência!');
    setTimeout(() => setCopiedIp(false), 2000);
  };

  const getExtratoMonths = () => {
    const now = new Date();
    const currentY = now.getFullYear();
    const currentM = now.getMonth() + 1;

    const prevD = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const prevY = prevD.getFullYear();
    const prevM = prevD.getMonth() + 1;

    const monthNames = [
      'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
      'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'
    ];

    return [
      { key: 'current', label: `${monthNames[currentM - 1]}/${currentY}`, ano: currentY, mes: currentM },
      { key: 'prev', label: `${monthNames[prevM - 1]}/${prevY}`, ano: prevY, mes: prevM }
    ];
  };

  const extratoMonths = getExtratoMonths();

  const loadExtrato = useCallback(async (ano, mes) => {
    if (!conversation?.customer_contract) return;
    setExtratoLoading(true);
    try {
      const currentCpf = conversation.customer_cpf || customerData.customer_cpf || (sgpData?.contracts?.[0]?.cpfcnpj) || '';
      const res = await getSGPExtratoUso({
        contrato: conversation.customer_contract,
        cpfcnpj: currentCpf,
        ano,
        mes
      });
      if (res.data && res.data.success) {
        setExtratoData(res.data.data);
      } else {
        setExtratoData(null);
      }
    } catch (err) {
      console.error('Error loading extrato uso:', err);
      setExtratoData(null);
    } finally {
      setExtratoLoading(false);
    }
  }, [conversation?.customer_contract, conversation?.customer_cpf, customerData.customer_cpf, sgpData?.contracts]);

  useEffect(() => {
    if (conversation?.customer_contract) {
      const target = extratoMonths[activeExtratoTab];
      loadExtrato(target.ano, target.mes);
    }
  }, [conversation?.customer_contract, activeExtratoTab, loadExtrato]);

  const extractTrafficList = (data) => {
    if (!data) return [];
    let parsed = data;
    if (typeof parsed === 'string') {
      try {
        parsed = JSON.parse(parsed);
      } catch (e) {
        return [];
      }
    }
    if (Array.isArray(parsed)) return parsed;
    if (parsed.list && Array.isArray(parsed.list)) return parsed.list;
    if (parsed.extrato && Array.isArray(parsed.extrato)) return parsed.extrato;
    if (parsed.extratouso && Array.isArray(parsed.extratouso)) return parsed.extratouso;
    if (parsed.registros && Array.isArray(parsed.registros)) return parsed.registros;
    if (parsed.rows && Array.isArray(parsed.rows)) return parsed.rows;
    if (parsed.items && Array.isArray(parsed.items)) return parsed.items;
    if (parsed.dados && Array.isArray(parsed.dados)) return parsed.dados;
    if (parsed.uso && Array.isArray(parsed.uso)) return parsed.uso;
    if (parsed.result && Array.isArray(parsed.result)) return parsed.result;
    if (parsed.results && Array.isArray(parsed.results)) return parsed.results;
    if (parsed.lista && Array.isArray(parsed.lista)) return parsed.lista;
    if (parsed.connections && Array.isArray(parsed.connections)) return parsed.connections;

    if (typeof parsed === 'object' && parsed !== null) {
      const vals = Object.values(parsed).filter(v => typeof v === 'object' && v !== null);
      if (vals.length > 0) return vals;
    }
    return [];
  };

  const getCustomerIP = () => {
    // 1. Check extratoData for recent connection IP
    const list = extractTrafficList(extratoData);
    if (list && list.length > 0) {
      for (const item of list) {
        const ipVal = item.ip || item.ip_conexao || item.framedipaddress || item.ip_address || item.last_ip || item.ip_atual || item.ipv4;
        if (ipVal && typeof ipVal === 'string' && ipVal.trim()) {
          return ipVal.trim();
        }
      }
    }
    if (extratoData && typeof extratoData === 'object') {
      const ipVal = extratoData.ip || extratoData.ip_conexao || extratoData.framedipaddress || extratoData.ip_address || extratoData.last_ip || extratoData.ip_atual || extratoData.ipv4;
      if (ipVal && typeof ipVal === 'string' && ipVal.trim()) {
        return ipVal.trim();
      }
    }
    // 2. Check SGP contract data
    if (sgpData?.contracts) {
      const selectedContract = sgpData.contracts.find(c => String(c.contrato) === String(conversation?.customer_contract)) || sgpData.contracts[0];
      if (selectedContract) {
        const ipVal = selectedContract.ip || selectedContract.ip_conexao || selectedContract.framedipaddress || selectedContract.ip_address || selectedContract.ip_atual || selectedContract.last_ip;
        if (ipVal && typeof ipVal === 'string' && ipVal.trim()) {
          return ipVal.trim();
        }
      }
    }
    return null;
  };

  const formatBytesOrVal = (val) => {
    if (val === null || val === undefined) return '-';
    if (typeof val === 'number') {
      if (val === 0) return '0 B';
      const k = 1000;
      const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
      const i = Math.min(Math.floor(Math.log(val) / Math.log(k)), sizes.length - 1);
      return `${(val / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
    }
    if (typeof val === 'string') {
      if (!isNaN(val) && val.trim() !== '') {
        return formatBytesOrVal(Number(val));
      }
      return val;
    }
    return String(val);
  };

  const formatSecondsToHours = (seconds) => {
    if (!seconds) return '';
    const sec = Number(seconds);
    if (isNaN(sec) || sec <= 0) return '';
    const hrs = Math.floor(sec / 3600);
    const mins = Math.floor((sec % 3600) / 60);
    if (hrs > 0) return `${hrs}h ${mins}m`;
    return `${mins}m`;
  };

  const formatConnectionDate = (isoStr) => {
    if (!isoStr) return '';
    try {
      const d = new Date(isoStr);
      if (isNaN(d.getTime())) return isoStr;
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const hours = String(d.getHours()).padStart(2, '0');
      const mins = String(d.getMinutes()).padStart(2, '0');
      return `${day}/${month} ${hours}:${mins}`;
    } catch {
      return isoStr;
    }
  };

  const detectInstability = (data) => {
    const list = extractTrafficList(data);
    if (!list || list.length === 0) return null;

    const dayCounts = {};
    list.forEach(item => {
      const rawDate = item.data || item.dataini || item.data_uso || item.dia || item.date || '';
      if (!rawDate) return;

      const dayKey = rawDate.split(' ')[0].split('T')[0];
      if (dayKey) {
        dayCounts[dayKey] = (dayCounts[dayKey] || 0) + 1;
      }
    });

    const unstableDays = Object.entries(dayCounts).filter(([_, count]) => count >= 5);
    if (unstableDays.length > 0) {
      unstableDays.sort((a, b) => b[1] - a[1]);
      const [topDay, topCount] = unstableDays[0];
      let formattedDay = topDay;
      if (topDay.includes('-')) {
        const parts = topDay.split('-');
        if (parts.length === 3) formattedDay = `${parts[2]}/${parts[1]}/${parts[0]}`;
      }
      return {
        hasInstability: true,
        date: formattedDay,
        count: topCount,
        totalUnstableDays: unstableDays.length
      };
    }
    return null;
  };

  const getCurrentDateTimeLocal = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const loadOSLists = async () => {
    setLoadingOSLists(true);
    try {
      const [motivosRes, tiposRes] = await Promise.all([
        getSGPMotivosOS().catch((err) => ({ data: { error: err.message } })),
        getSGPTiposOcorrencia().catch((err) => ({ data: { error: err.message } }))
      ]);

      const extractOptions = (resData) => {
        if (!resData) return [];
        let payload = resData.data !== undefined ? resData.data : resData;
        if (typeof payload === 'string') {
          try { payload = JSON.parse(payload); } catch (e) {}
        }
        if (payload && payload.data !== undefined && !Array.isArray(payload)) {
          let innerData = payload.data;
          if (typeof innerData === 'string') {
            try { innerData = JSON.parse(innerData); } catch (e) {}
          }
          if (Array.isArray(innerData)) return innerData;
          if (innerData && typeof innerData === 'object') payload = innerData;
        }

        if (Array.isArray(payload)) return payload;

        if (payload && typeof payload === 'object') {
          if (Array.isArray(payload.motivos)) return payload.motivos;
          if (Array.isArray(payload.tipos)) return payload.tipos;
          if (Array.isArray(payload.result)) return payload.result;
          if (Array.isArray(payload.items)) return payload.items;
          if (Array.isArray(payload.registros)) return payload.registros;
          if (Array.isArray(payload.rows)) return payload.rows;

          // Check for key-value dictionary mappings (e.g., { "1": "Suporte Técnico", "2": "Instalação" })
          const keys = Object.keys(payload);
          if (keys.length > 0) {
            const isDict = keys.every(k => !isNaN(k)) || keys.every(k => typeof payload[k] === 'string' || (payload[k] && typeof payload[k] === 'object'));
            if (isDict) {
              return keys.map(k => {
                const val = payload[k];
                if (typeof val === 'string') {
                  return { id: k, descricao: val };
                }
                if (val && typeof val === 'object') {
                  return { id: k, ...(val.id ? {} : { id: k }), ...val };
                }
                return { id: k, descricao: String(val) };
              });
            }
          }
        }
        return [];
      };

      setMotivosList(extractOptions(motivosRes.data));
      setTiposList(extractOptions(tiposRes.data));
    } catch (error) {
      console.error('Error loading OS lists:', error);
      toast.error('Erro ao carregar motivos e tipos de OS');
    } finally {
      setLoadingOSLists(false);
    }
  };

  const handleOpenOSModal = async () => {
    const currentContract = conversation?.customer_contract || customerData.customer_contract || '';
    let currentBairro = conversation?.customer_bairro || conversation?.bairro || customerData?.customer_bairro || '';

    let activeSgpData = sgpData;
    if (!activeSgpData && (customerData.customer_cpf || conversation?.customer_cpf)) {
      const cpfToSearch = customerData.customer_cpf || conversation?.customer_cpf;
      try {
        const res = await consultarClienteSGP(cpfToSearch);
        if (res.data?.found) {
          activeSgpData = res.data;
          setSgpData(res.data);
        }
      } catch (err) {
        console.error('SGP auto search error on OS open:', err);
      }
    }

    if (activeSgpData?.contracts && Array.isArray(activeSgpData.contracts)) {
      const match = activeSgpData.contracts.find(c => String(c.contrato || c.id || '') === String(currentContract));
      if (match && (match.bairro || match.endereco_bairro || match.bairro_nome)) {
        currentBairro = match.bairro || match.endereco_bairro || match.bairro_nome;
      } else if (activeSgpData.contracts.length > 0) {
        const first = activeSgpData.contracts[0];
        if (first.bairro || first.endereco_bairro || first.bairro_nome) {
          currentBairro = first.bairro || first.endereco_bairro || first.bairro_nome;
        }
      }
    }

    setOsContrato(currentContract);
    setOsBairro(currentBairro);
    setOsAgendamento(getCurrentDateTimeLocal());
    setIsOSModalOpen(true);
    loadOSLists();
  };

  const handleCreateOS = async (e) => {
    e?.preventDefault();
    if (!osContrato) {
      toast.error('Por favor, informe o número do contrato');
      return;
    }
    if (!selectedTipo) {
      toast.error('Por favor, selecione o Tipo de Ocorrência');
      return;
    }
    if (!selectedMotivo) {
      toast.error('Por favor, selecione o Motivo da OS');
      return;
    }
    if (!osConteudo.trim()) {
      toast.error('Por favor, informe o conteúdo da ocorrência');
      return;
    }

    setSubmittingOS(true);
    try {
      const formattedAgendamento = osAgendamento ? osAgendamento.replace('T', ' ') : '';
      const response = await abrirOrdemServicoSGP({
        conversation_id: conversation?.id,
        contrato: osContrato,
        ocorrenciatipo: selectedTipo,
        motivoos: selectedMotivo,
        conteudo: osConteudo.trim(),
        observacao: osObservacao.trim(),
        bairro: osBairro.trim(),
        conteudolimpo: 1,
        data_hora_agendamento: formattedAgendamento
      });

      if (response.data?.success) {
        toast.success(response.data?.message || 'Ordem de Serviço criada com sucesso!');
        setIsOSModalOpen(false);
        setOsConteudo('');
        setOsObservacao('');
        setOsAgendamento('');
      } else {
        toast.error(response.data?.error || 'Erro ao criar Ordem de Serviço');
      }
    } catch (error) {
      console.error('Error creating OS:', error);
      toast.error(error.response?.data?.detail || 'Erro ao comunicar com o servidor');
    } finally {
      setSubmittingOS(false);
    }
  };

  const normalizeCpf = (value) => {
    if (!value) return '';
    return String(value).replace(/\D/g, '');
  };

  // Reset states when conversation changes
  useEffect(() => {
    if (conversation?.id !== lastConversationId) {
      // New conversation - reset SGP states
      setSgpData(null);
      setSgpError(null);
      setShowContractSelector(false);
      setLastSearchedCpf('');
      setLastConversationId(conversation?.id || '');
      setContratoStatusText(null);
      setShowUnlockCalendar(false);
      if (conversation?.id) {
        const cpfKey = normalizeCpf(conversation.customer_cpf);
        const cache = financeiroCacheRef.current[conversation.id];
        if (cache && cache.cpf === cpfKey && cache.contract === conversation.customer_contract) {
          setFinanceiroData(cache.data);
        } else {
          setFinanceiroData(null);
        }
      } else {
        setFinanceiroData(null);
      }
    }

    if (conversation) {
      setCustomerData({
        customer_name: conversation.customer_name || '',
        customer_cpf: conversation.customer_cpf || '',
        customer_plan: conversation.customer_plan || '',
        customer_status: conversation.customer_status || '',
        customer_contract: conversation.customer_contract || '',
      });
    }
  }, [conversation?.id, lastConversationId]);

  const hasMultipleContracts = (sgpData?.contracts?.length || 0) > 1;

  // Check access status for a contract
  const checkAccessStatus = useCallback(async (contrato, options = {}) => {
    if (!contrato) return;
    const { silent = false } = options;

    setStatusLoading(true);
    try {
      const response = await verificarAcessoSGP(contrato);
      const data = response.data;

      if (data.success && data.status) {
        // Update the customer status with real-time data
        const newStatus = data.status === 'online' ? 'online' : 'offline';
        onUpdateConversation({ customer_status: newStatus }, { silent });

        if (!silent) {
          if (data.status === 'online') {
            toast.success('Cliente está Online');
          } else if (data.status === 'offline') {
            toast.error('Cliente está Offline');
          }
        }
      }
    } catch (error) {
      console.error('Error checking access status:', error);
    } finally {
      setStatusLoading(false);
    }
  }, [onUpdateConversation]);

  const handleSelectContract = useCallback(async (contract, options = {}) => {
    const { showToast = true, silentUpdate = false } = options;
    const contratoId = String(contract.contrato || contract.id || '');
    const contratoBairro = contract.bairro || contract.endereco_bairro || contract.bairro_nome || '';

    // Update conversation with SGP data (initial status from consultacliente)
    const updateData = {
      customer_name: contract.nome || customerData.customer_name,
      customer_plan: contract.plano || '',
      customer_status: contract.status_conexao || contract.status || '',
      customer_contract: contratoId,
      customer_bairro: contratoBairro,
    };

    // Store contract status text from API response
    if (contract.status) {
      setContratoStatusText(contract.status);
    }

    onUpdateConversation(updateData, { silent: silentUpdate });
    setShowContractSelector(false);
    if (showToast) {
      toast.success('Dados do cliente atualizados');
    }

    // Now check real-time access status using verificaacesso API
    if (contratoId) {
      await checkAccessStatus(contratoId, { silent: silentUpdate });
    }
  }, [checkAccessStatus, customerData.customer_name, onUpdateConversation]);

  // Search SGP when CPF changes
  const searchSGP = useCallback(async (cpf, forceSearch = false) => {
    if (!cpf || cpf.length < 11) {
      setSgpData(null);
      setSgpError(null);
      return;
    }

    // Clean CPF for comparison
    const cleanCpf = cpf.replace(/\D/g, '');

    // Skip if already searched this CPF (unless forced)
    if (!forceSearch && cleanCpf === lastSearchedCpf) return;

    setSgpLoading(true);
    setSgpError(null);
    setLastSearchedCpf(cleanCpf);

    try {
      const response = await consultarClienteSGP(cpf);
      const data = response.data;

      if (data.found && data.contracts && data.contracts.length > 0) {
        setSgpData(data);
        const hasMultiple = data.multiple || data.contracts.length > 1;

        // Always update contract status text from API response
        const currentContractId = String(conversation?.customer_contract || '');
        if (currentContractId) {
          const matchingContract = data.contracts.find(
            c => String(c.contrato || c.id || '') === currentContractId
          );
          if (matchingContract && matchingContract.status) {
            setContratoStatusText(matchingContract.status);
          }
        }

        if (hasMultiple) {
          const conversationId = conversation?.id || 'unknown';
          const lastPromptedCpf = promptedCpfRef.current[conversationId];
          if (lastPromptedCpf !== cleanCpf) {
            // Multiple contracts - show selector
            setShowContractSelector(true);
            promptedCpfRef.current[conversationId] = cleanCpf;
          }
          return;
        }

        // Single contract - auto-fill or refresh status
        const contract = data.contracts[0];
        const contratoId = String(contract.contrato || contract.id || '');
        if (!contratoId) {
          return;
        }

        if (!conversation?.customer_contract || String(conversation.customer_contract) !== contratoId) {
          await handleSelectContract(contract, { showToast: false, silentUpdate: true });
        } else {
          await checkAccessStatus(contratoId, { silent: true });
        }
      } else {
        setSgpData(null);
        setSgpError(data.message || 'Cliente não encontrado');
      }
    } catch (error) {
      console.error('SGP search error:', error);
      setSgpError('Erro ao consultar SGP');
      setSgpData(null);
    } finally {
      setSgpLoading(false);
    }
  }, [
    checkAccessStatus,
    conversation?.customer_contract,
    conversation?.id,
    handleSelectContract,
    lastSearchedCpf
  ]);

  // Watch for CPF changes and search
  useEffect(() => {
    // Auto-search if conversation has CPF
    if (conversation?.customer_cpf) {
      searchSGP(conversation.customer_cpf);
    } else if (!conversation?.customer_cpf) {
      setSgpData(null);
      setSgpError(null);
      setLastSearchedCpf('');
    }
  }, [conversation?.id, conversation?.customer_cpf, searchSGP]);

  // Function to manually refresh status
  const handleRefreshStatus = async () => {
    if (conversation?.customer_contract) {
      await checkAccessStatus(conversation.customer_contract);
    } else {
      toast.error('Nenhum contrato selecionado');
    }
  };

  // Financial functions
  const loadFinanceiro = async () => {
    if (!conversation?.customer_contract || !conversation?.id) return;
    const conversationId = conversation.id;
    const contract = conversation.customer_contract;
    const cpfKey = normalizeCpf(conversation.customer_cpf);

    setFinanceiroLoading(true);
    try {
      const response = await consultarFinanceiroSGP(contract);
      const data = response.data;

      if (data.success) {
        if (conversationId !== conversation?.id || contract !== conversation?.customer_contract) {
          return;
        }
        setFinanceiroData(data);
        financeiroCacheRef.current[conversationId] = {
          cpf: cpfKey,
          contract,
          data
        };
      } else {
        setFinanceiroData(null);
      }
    } catch (error) {
      console.error('Error loading financial data:', error);
      setFinanceiroData(null);
    } finally {
      setFinanceiroLoading(false);
    }
  };

  // Load financial data when contract changes
  useEffect(() => {
    if (!conversation?.id || !conversation?.customer_contract) {
      setFinanceiroData(null);
      return;
    }
    const cpfKey = normalizeCpf(conversation.customer_cpf);
    const cache = financeiroCacheRef.current[conversation.id];
    if (cache && cache.cpf === cpfKey && cache.contract === conversation.customer_contract) {
      setFinanceiroData(cache.data);
      return;
    }
    loadFinanceiro();
  }, [conversation?.id, conversation?.customer_contract, conversation?.customer_cpf]);

  const handleEnviarBoleto = async () => {
    if (!conversation?.customer_contract || !conversation?.id) {
      toast.error('Contrato não selecionado');
      return;
    }

    setSendingBoleto(true);
    try {
      const response = await enviarBoletoSGP(conversation.id, conversation.customer_contract);
      if (response.data.success) {
        toast.success('Boleto enviado com sucesso!');
      } else {
        toast.error(response.data.error || 'Erro ao enviar boleto');
      }
    } catch (error) {
      console.error('Error sending boleto:', error);
      toast.error('Erro ao enviar boleto');
    } finally {
      setSendingBoleto(false);
    }
  };

  const handleEnviarPix = async () => {
    if (!conversation?.customer_contract || !conversation?.id) {
      toast.error('Contrato não selecionado');
      return;
    }

    setSendingPix(true);
    try {
      const response = await enviarPixSGP(conversation.id, conversation.customer_contract);
      if (response.data.success) {
        toast.success('Código PIX enviado com sucesso!');
      } else {
        toast.error(response.data.error || 'Erro ao enviar código PIX');
      }
    } catch (error) {
      console.error('Error sending PIX:', error);
      toast.error('Erro ao enviar código PIX');
    } finally {
      setSendingPix(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr + 'T00:00:00');
      return date.toLocaleDateString('pt-BR');
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (value) => {
    if (value === undefined || value === null) return 'R$ 0,00';
    return `R$ ${Number(value).toFixed(2).replace('.', ',')}`;
  };

  const handleSaveCustomer = () => {
    onUpdateConversation(customerData);
    setIsEditingCustomer(false);
  };

  const handleCpfChange = async (newCpf) => {
    setCustomerData({ ...customerData, customer_cpf: newCpf });
  };

  const handleSaveCpf = async () => {
    // Clear contract when searching new CPF
    await onUpdateConversation({
      customer_cpf: customerData.customer_cpf,
      customer_contract: '',  // Clear contract to trigger new search
      customer_name: '',
      customer_plan: '',
      customer_status: ''
    });
    // Force search with new CPF
    setLastSearchedCpf('');
    searchSGP(customerData.customer_cpf, true);
  };

  const handleAddNote = () => {
    if (!newNote.trim()) return;
    onAddNote(newNote.trim());
    setNewNote('');
  };

  // Unlock handler
  const handleLiberarPromessa = async (selectedDate) => {
    if (!conversation?.customer_contract || !conversation?.id) {
      toast.error('Contrato não selecionado');
      return;
    }

    setUnlockLoading(true);
    setShowUnlockCalendar(false);
    try {
      // Format date as dd/MM/yyyy (frontend display)
      const d = selectedDate;
      const formatted = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;

      // API requires yyyy-MM-dd
      const formattedApi = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

      const response = await liberarPromessaSGP(
        conversation.id,
        conversation.customer_contract,
        formattedApi
      );

      if (response.data.success) {
        toast.success(`Desbloqueio realizado! Protocolo: ${response.data.protocolo}`);
        // Refresh status after unlock
        await checkAccessStatus(conversation.customer_contract);
        // Update contract status locally (service was released)
        setContratoStatusText('Ativo');
      } else {
        toast.error(response.data.error || response.data.msg || 'Erro ao liberar contrato');
      }
    } catch (error) {
      console.error('Error unlocking contract:', error);
      toast.error('Erro ao liberar contrato');
    } finally {
      setUnlockLoading(false);
    }
  };

  if (!conversation) {
    return (
      <div className="customer-panel" data-testid="customer-panel-empty">
        <div className="flex-1 flex items-center justify-center">
          <p className="text-sm text-muted-foreground">
            Selecione uma conversa
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="customer-panel" data-testid="customer-panel">
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-6">
          {/* Customer Header */}
          <div className="text-center">
            <Avatar className="w-16 h-16 mx-auto mb-3 bg-secondary">
              <AvatarFallback className="bg-secondary text-muted-foreground text-xl">
                {getInitials(conversation.customer_name)}
              </AvatarFallback>
            </Avatar>
            <h3 className="font-semibold text-lg">
              {conversation.customer_name || 'Cliente'}
            </h3>
            <p className="text-sm text-muted-foreground">{conversation.customer_phone}</p>

            {/* Connection Status Badge */}
            {conversation.customer_status && (
              <div className="flex items-center justify-center gap-2 mt-2">
                {conversation.customer_status === 'online' ? (
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/15">
                    <Wifi size={12} className="mr-1" />
                    Online
                  </Badge>
                ) : conversation.customer_status === 'offline' ? (
                  <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                    <WifiOff size={12} className="mr-1" />
                    Offline
                  </Badge>
                ) : (
                  <Badge variant="secondary">{conversation.customer_status}</Badge>
                )}
              </div>
            )}
          </div>

          <Separator />

          {/* Customer Info */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium font-mono text-sm">Dados do Cliente</h4>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsEditingCustomer(!isEditingCustomer)}
                data-testid="edit-customer-btn"
              >
                {isEditingCustomer ? 'Cancelar' : 'Editar'}
              </Button>
            </div>

            {isEditingCustomer ? (
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-muted-foreground">Nome</label>
                  <Input
                    value={customerData.customer_name}
                    onChange={(e) => setCustomerData({ ...customerData, customer_name: e.target.value })}
                    className="bg-secondary border-0 h-8 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">CPF/CNPJ</label>
                  <div className="flex gap-2">
                    <Input
                      value={customerData.customer_cpf}
                      onChange={(e) => handleCpfChange(e.target.value)}
                      className="bg-secondary border-0 h-8 text-sm"
                      placeholder="000.000.000-00"
                    />
                    <Button
                      size="icon"
                      variant="secondary"
                      className="h-8 w-8"
                      onClick={handleSaveCpf}
                      disabled={sgpLoading}
                    >
                      {sgpLoading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                    </Button>
                  </div>
                  {sgpError && (
                    <p className="text-xs text-yellow-400 mt-1 flex items-center gap-1">
                      <AlertCircle size={12} />
                      {sgpError}
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Contrato</label>
                  <Input
                    value={customerData.customer_contract}
                    onChange={(e) => setCustomerData({ ...customerData, customer_contract: e.target.value })}
                    className="bg-secondary border-0 h-8 text-sm"
                    placeholder="Número do contrato"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Plano</label>
                  <Input
                    value={customerData.customer_plan}
                    onChange={(e) => setCustomerData({ ...customerData, customer_plan: e.target.value })}
                    className="bg-secondary border-0 h-8 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Status</label>
                  <Input
                    value={customerData.customer_status}
                    onChange={(e) => setCustomerData({ ...customerData, customer_status: e.target.value })}
                    className="bg-secondary border-0 h-8 text-sm"
                  />
                </div>
                <Button
                  onClick={handleSaveCustomer}
                  size="sm"
                  className="w-full"
                  data-testid="save-customer-btn"
                >
                  <Save size={14} className="mr-2" />
                  Salvar
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <InfoRow icon={User} label="Nome" value={conversation.customer_name || '-'} />
                <div className="flex items-center gap-3">
                  <FileText size={14} className="text-muted-foreground" />
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground">CPF/CNPJ</p>
                    <div className="flex items-center gap-2">
                      <p className="text-sm">{conversation.customer_cpf || '-'}</p>
                      {sgpLoading && <Loader2 size={12} className="animate-spin text-primary" />}
                      {sgpData?.found && !sgpLoading && (
                        <CheckCircle size={12} className="text-green-400" />
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Hash size={14} className="text-muted-foreground" />
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground">Contrato</p>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium">{conversation.customer_contract || '-'}</p>
                      {contratoStatusText && (
                        <span className={cn(
                          "text-[10px] px-1.5 py-0.5 rounded-full font-medium",
                          String(contratoStatusText).toLowerCase() === 'ativo' && "bg-green-500/20 text-green-400",
                          String(contratoStatusText).toLowerCase() === 'suspenso' && "bg-red-500/20 text-red-400",
                          !['ativo', 'suspenso'].includes(String(contratoStatusText).toLowerCase()) && "bg-yellow-500/20 text-yellow-400"
                        )}>
                          {contratoStatusText}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <InfoRow
                  icon={conversation.customer_status === 'online' ? Wifi : WifiOff}
                  label="Plano"
                  value={conversation.customer_plan || '-'}
                />
                {/* IP Conexão with Copy button */}
                {(() => {
                  const currentIp = getCustomerIP();
                  const showIp = conversation.customer_status === 'online' ? currentIp : (currentIp || null);
                  return (
                    <div className="flex items-center gap-3">
                      <Globe size={14} className="text-muted-foreground" />
                      <div className="flex-1">
                        <p className="text-xs text-muted-foreground">IP Conexão</p>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-mono">{showIp || '-'}</p>
                          {showIp && (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-5 w-5 text-muted-foreground hover:text-foreground"
                              onClick={() => handleCopyIp(showIp)}
                              title="Copiar IP"
                            >
                              {copiedIp ? <Check size={12} className="text-green-400" /> : <Copy size={12} />}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })()}
                {/* Status with refresh button */}
                <div className="flex items-center gap-3">
                  {conversation.customer_status === 'online' ? (
                    <Wifi size={14} className="text-green-400" />
                  ) : conversation.customer_status === 'offline' ? (
                    <WifiOff size={14} className="text-red-400" />
                  ) : (
                    <Clock size={14} className="text-muted-foreground" />
                  )}
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground">Status</p>
                    <div className="flex items-center gap-2">
                      <p className={cn(
                        "text-sm font-medium",
                        conversation.customer_status === 'online' && "text-green-400",
                        conversation.customer_status === 'offline' && "text-red-400"
                      )}>
                        {conversation.customer_status === 'online' ? 'Online' :
                          conversation.customer_status === 'offline' ? 'Offline' :
                            conversation.customer_status || '-'}
                      </p>
                      {statusLoading && <Loader2 size={12} className="animate-spin text-primary" />}
                    </div>
                  </div>
                  {conversation.customer_contract && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-6 w-6"
                      onClick={handleRefreshStatus}
                      disabled={statusLoading}
                      title="Atualizar status"
                    >
                      <RefreshCw size={12} className={cn(statusLoading && "animate-spin")} />
                    </Button>
                  )}
                </div>
                {hasMultipleContracts && (
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setShowContractSelector(true)}
                      disabled={sgpLoading}
                      data-testid="switch-contract-btn"
                    >
                      <Repeat2 size={12} className="mr-2" />
                      Trocar contrato
                    </Button>
                  </div>
                )}

                {/* Unlock button for suspended contracts */}
                {contratoStatusText && String(contratoStatusText).toLowerCase() === 'suspenso' && conversation.customer_contract && (
                  <Popover open={showUnlockCalendar} onOpenChange={setShowUnlockCalendar}>
                    <PopoverTrigger asChild>
                      <Button
                        size="sm"
                        variant="destructive"
                        className="w-full"
                        disabled={unlockLoading}
                        data-testid="unlock-contract-btn"
                      >
                        {unlockLoading ? (
                          <Loader2 size={14} className="mr-2 animate-spin" />
                        ) : (
                          <Unlock size={14} className="mr-2" />
                        )}
                        Desbloquear Contrato
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="center">
                      <div className="p-3 border-b border-border">
                        <p className="text-sm font-medium">Promessa de Pagamento</p>
                        <p className="text-xs text-muted-foreground">Selecione a data da promessa (máx. {promessaDias} dias)</p>
                      </div>
                      <Calendar
                        mode="single"
                        selected={null}
                        onSelect={(date) => {
                          if (date) handleLiberarPromessa(date);
                        }}
                        disabled={(date) => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const maxDate = new Date(today);
                          maxDate.setDate(maxDate.getDate() + promessaDias);
                          return date < today || date > maxDate;
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            )}
          </div>

          <Separator />

          {/* Financeiro */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-medium font-mono text-sm">Financeiro</h4>
              {conversation?.customer_contract && (
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-6 w-6"
                  onClick={loadFinanceiro}
                  disabled={financeiroLoading}
                  title="Atualizar financeiro"
                >
                  <RefreshCw size={12} className={cn(financeiroLoading && "animate-spin")} />
                </Button>
              )}
            </div>

            {!conversation?.customer_contract ? (
              <p className="text-xs text-muted-foreground">Selecione um contrato para ver dados financeiros</p>
            ) : financeiroLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 size={20} className="animate-spin text-primary" />
              </div>
            ) : !financeiroData?.has_bills ? (
              <p className="text-xs text-muted-foreground">Nenhuma fatura encontrada</p>
            ) : (
              <div className="space-y-3">
                {/* Status Financeiro */}
                <div className="flex items-center gap-3">
                  <DollarSign size={14} className={cn(
                    financeiroData.is_overdue ? "text-red-400" : "text-green-400"
                  )} />
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground">Status</p>
                    <p className={cn(
                      "text-sm font-medium",
                      financeiroData.is_overdue ? "text-red-400" : "text-green-400"
                    )}>
                      {financeiroData.is_overdue ? 'Em Atraso' : 'Em Dia'}
                    </p>
                  </div>
                </div>

                {/* Vencimento */}
                <div className="flex items-center gap-3">
                  <CalendarIcon size={14} className="text-muted-foreground" />
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground">
                      {financeiroData.is_overdue ? 'Vencido em' : 'Vencimento'}
                    </p>
                    <p className="text-sm">{formatDate(financeiroData.vencimento)}</p>
                  </div>
                </div>

                {/* Valor */}
                <div className="flex items-center gap-3">
                  <DollarSign size={14} className="text-muted-foreground" />
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground">Valor</p>
                    <p className="text-sm font-medium">{formatCurrency(financeiroData.valor)}</p>
                    {financeiroData.multa > 0 || financeiroData.juros > 0 ? (
                      <p className="text-xs text-red-400">
                        Multa: {formatCurrency(financeiroData.multa)} | Juros: {formatCurrency(financeiroData.juros)}
                      </p>
                    ) : null}
                  </div>
                </div>

                {/* Botões de Ação */}
                <div className="flex gap-2 pt-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    className="flex-1"
                    onClick={handleEnviarBoleto}
                    disabled={sendingBoleto || !financeiroData.link_boleto}
                  >
                    {sendingBoleto ? (
                      <Loader2 size={14} className="mr-2 animate-spin" />
                    ) : (
                      <Send size={14} className="mr-2" />
                    )}
                    Enviar Boleto
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="flex-1"
                    onClick={handleEnviarPix}
                    disabled={sendingPix || !financeiroData.codigo_pix}
                  >
                    {sendingPix ? (
                      <Loader2 size={14} className="mr-2 animate-spin" />
                    ) : (
                      <QrCode size={14} className="mr-2" />
                    )}
                    Enviar PIX
                  </Button>
                </div>
              </div>
            )}
          </div>

          <Separator />

          {/* Ordem de Serviço */}
          <div className="space-y-3">
            <h4 className="font-medium font-mono text-sm flex items-center gap-2">
              <Wrench size={16} className="text-primary" />
              Ordem de Serviço (SGP)
            </h4>
            <p className="text-xs text-muted-foreground">
              Abra uma nova Ordem de Serviço ou chamado técnico no SGP.
            </p>
            <Button
              size="sm"
              className="w-full bg-primary hover:bg-primary/90 text-white shadow-sm"
              onClick={handleOpenOSModal}
              data-testid="abrir-os-btn"
            >
              <Wrench size={14} className="mr-2" />
              Abrir Ordem de Serviço
            </Button>
          </div>

          <Separator />

          {/* Extrato de Tráfego */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-medium font-mono text-sm flex items-center gap-2">
                <Activity size={16} className="text-primary" />
                Extrato de Tráfego
              </h4>
              {conversation?.customer_contract && (
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-6 w-6"
                  onClick={() => {
                    const target = extratoMonths[activeExtratoTab];
                    loadExtrato(target.ano, target.mes);
                  }}
                  disabled={extratoLoading}
                  title="Atualizar extrato"
                >
                  <RefreshCw size={12} className={cn(extratoLoading && "animate-spin")} />
                </Button>
              )}
            </div>

            {!conversation?.customer_contract ? (
              <p className="text-xs text-muted-foreground">Selecione um contrato para ver o extrato de tráfego</p>
            ) : (
              <div className="space-y-3">
                {/* Abas dos últimos 2 meses */}
                <div className="flex bg-secondary/50 p-1 rounded-lg gap-1 border border-border">
                  {extratoMonths.map((m, idx) => (
                    <button
                      key={m.key}
                      onClick={() => setActiveExtratoTab(idx)}
                      className={cn(
                        "flex-1 text-xs py-1.5 px-2 rounded-md font-medium transition-all text-center",
                        activeExtratoTab === idx
                          ? "bg-primary text-white shadow-sm"
                          : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                      )}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>

                {/* Instability Alert Banner (5+ disconnections on same day) */}
                {(() => {
                  const alert = detectInstability(extratoData);
                  if (!alert) return null;
                  return (
                    <div className="p-3 rounded-lg bg-red-950/80 border border-red-500/60 flex items-start gap-2.5 text-xs text-red-100 shadow-sm">
                      <AlertTriangle size={18} className="text-red-400 shrink-0 mt-0.5 animate-pulse" />
                      <div className="flex-1">
                        <p className="font-bold text-red-200 text-xs">Alerta de Instabilidade</p>
                        <p className="text-[12px] text-red-100 font-medium mt-0.5 leading-snug">
                          Identificado <strong className="text-white underline">{alert.count} desconexões/reconexões</strong> no dia <strong className="text-white">{alert.date}</strong>.
                        </p>
                      </div>
                    </div>
                  );
                })()}

                {extratoLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 size={18} className="animate-spin text-primary mr-2" />
                    <span className="text-xs text-muted-foreground">Carregando extrato...</span>
                  </div>
                ) : (
                  <div>
                    {(() => {
                      const list = extractTrafficList(extratoData);
                      const hasSummary = extratoData && typeof extratoData === 'object' && (extratoData.login || extratoData.plano || extratoData.total !== undefined);

                      if ((!list || list.length === 0) && !hasSummary) {
                        return (
                          <div className="p-3 text-center rounded-lg border border-dashed border-border bg-secondary/20">
                            <p className="text-xs text-muted-foreground">Sem registros de tráfego para este período.</p>
                          </div>
                        );
                      }

                      return (
                        <div className="space-y-2">
                          {/* Summary badges if returned by SGP */}
                          {hasSummary && (
                            <div className="p-2 rounded-lg bg-secondary/60 border border-border text-xs space-y-1">
                              {extratoData.login && (
                                <div className="flex justify-between items-center">
                                  <span className="text-muted-foreground">PPPoE / Login:</span>
                                  <span className="font-mono font-medium">{extratoData.login}</span>
                                </div>
                              )}
                              {extratoData.plano && (
                                <div className="flex justify-between items-center">
                                  <span className="text-muted-foreground">Plano:</span>
                                  <span className="font-medium">{extratoData.plano}</span>
                                </div>
                              )}
                              {extratoData.total !== undefined && extratoData.total !== null && (
                                <div className="flex justify-between items-center">
                                  <span className="text-muted-foreground">Consumo Total:</span>
                                  <span className="font-semibold text-primary">{formatBytesOrVal(extratoData.total)}</span>
                                </div>
                              )}
                            </div>
                          )}

                          {list && list.length > 0 ? (
                            <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                              {list.map((item, i) => {
                                const isOnline = item.datafim === null;
                                const startDateFormatted = formatConnectionDate(item.dataini || item.data);
                                const endDateFormatted = item.datafim ? formatConnectionDate(item.datafim) : null;
                                const downVal = formatBytesOrVal(item.download);
                                const upVal = formatBytesOrVal(item.upload);
                                const totalVal = formatBytesOrVal(item.total);
                                const ipVal = item.ip || item.ip_conexao || item.framedipaddress;
                                const macVal = item.mac;
                                const durationVal = formatSecondsToHours(item.tempo);

                                return (
                                  <div key={i} className={cn(
                                    "p-2.5 rounded-md text-xs border transition-colors",
                                    isOnline
                                      ? "bg-green-500/10 border-green-500/30"
                                      : "bg-secondary/40 border-border/50"
                                  )}>
                                    <div className="flex justify-between items-center mb-1">
                                      <div className="flex items-center gap-1.5 font-medium">
                                        {isOnline ? (
                                          <Badge className="bg-green-500/20 text-green-400 border-green-500/20 text-[10px] px-1.5 py-0">
                                            🟢 Conectado
                                          </Badge>
                                        ) : (
                                          <span className="text-foreground">{startDateFormatted}</span>
                                        )}
                                        {endDateFormatted && (
                                          <span className="text-[11px] text-muted-foreground">➔ {endDateFormatted}</span>
                                        )}
                                      </div>
                                      <Badge variant="outline" className="text-[10px] bg-background font-mono font-medium">
                                        {totalVal}
                                      </Badge>
                                    </div>

                                    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                                      <div className="flex items-center gap-2">
                                        <span className="text-green-400 font-medium">↓ {downVal}</span>
                                        <span className="text-blue-400 font-medium">↑ {upVal}</span>
                                      </div>
                                      {durationVal && (
                                        <span className="text-amber-300 font-medium">⏱ {durationVal}</span>
                                      )}
                                    </div>

                                    {(ipVal || macVal) && (
                                      <div className="flex items-center gap-3 text-[10px] text-muted-foreground mt-1 pt-1 border-t border-border/30">
                                        {ipVal && <span>IP: <strong className="font-mono text-foreground">{ipVal}</strong></span>}
                                        {macVal && <span>MAC: <strong className="font-mono text-muted-foreground">{macVal}</strong></span>}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="p-2 text-center text-xs text-muted-foreground">
                              Sem lista detalhada de conexões.
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}
          </div>

          <Separator />

          {/* Timestamps */}
          <div className="space-y-2 text-xs text-muted-foreground">
            <p>Criado: {formatDateTime(conversation.created_at)}</p>
            <p>Atualizado: {formatDateTime(conversation.updated_at)}</p>
          </div>
        </div>
      </ScrollArea>

      {/* Contract Selector Dialog */}
      <Dialog open={showContractSelector} onOpenChange={setShowContractSelector}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-mono">Selecionar Contrato</DialogTitle>
            <DialogDescription>
              Foram encontrados múltiplos contratos. Selecione um para continuar.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 py-4 max-h-80 overflow-auto">
            {sgpData?.contracts?.map((contract, index) => (
              <button
                key={contract.id || index}
                onClick={() => handleSelectContract(contract)}
                className="w-full p-3 flex items-center gap-3 rounded-lg border border-border hover:bg-secondary/50 transition-colors text-left"
                data-testid={`contract-option-${index}`}
              >
                <div className={cn(
                  "w-10 h-10 rounded-full flex items-center justify-center",
                  contract.status_conexao === 'online' ? 'bg-green-500/20' : 'bg-red-500/20'
                )}>
                  {contract.status_conexao === 'online' ? (
                    <Wifi className="text-green-400" size={18} />
                  ) : (
                    <WifiOff className="text-red-400" size={18} />
                  )}
                </div>
                <div className="flex-1">
                  <p className="font-medium">{contract.nome}</p>
                  <p className="text-sm text-muted-foreground">
                    {contract.plano} • Contrato: {contract.contrato}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Status: {contract.status}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Ordem de Serviço Dialog */}
      <Dialog open={isOSModalOpen} onOpenChange={setIsOSModalOpen}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-800">
              <Wrench className="w-5 h-5 text-primary" />
              Abrir Ordem de Serviço
            </DialogTitle>
            <DialogDescription>
              Preencha os campos abaixo para registrar o chamado no SGP.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateOS} className="space-y-4 py-2">
            {/* Contrato */}
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">
                Contrato <span className="text-red-500">*</span>
              </label>
              <Input
                placeholder="Digite o número do contrato"
                value={osContrato}
                onChange={(e) => setOsContrato(e.target.value)}
                required
                className="bg-slate-50"
                data-testid="os-contrato-input"
              />
              {conversation?.customer_contract ? (
                <p className="text-[11px] text-muted-foreground mt-1">
                  * Preenchido automaticamente com o contrato do cliente na conversa.
                </p>
              ) : (
                <p className="text-[11px] text-amber-600 mt-1">
                  * Contrato não encontrado nos dados do cliente. Informe o contrato manualmente.
                </p>
              )}
            </div>

            {/* Tipo de Ocorrência */}
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">
                Tipo de Ocorrência <span className="text-red-500">*</span>
              </label>
              {loadingOSLists ? (
                <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
                  <Loader2 className="w-4 h-4 animate-spin text-primary" />
                  Carregando tipos de ocorrência...
                </div>
              ) : (
                <select
                  value={selectedTipo}
                  onChange={(e) => setSelectedTipo(e.target.value)}
                  className="w-full h-10 px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  required
                  data-testid="os-tipo-select"
                >
                  <option value="">-- Selecione o Tipo de Ocorrência --</option>
                  {tiposList.map((item, idx) => {
                    const id = item.id !== undefined ? item.id : (item.codigo !== undefined ? item.codigo : idx);
                    const label = item.descricao || item.nome || item.label || item.tipo || `Tipo ${id}`;
                    return (
                      <option key={idx} value={id}>
                        {label} (Código: {id})
                      </option>
                    );
                  })}
                </select>
              )}
            </div>

            {/* Motivo da OS */}
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">
                Motivo da OS <span className="text-red-500">*</span>
              </label>
              {loadingOSLists ? (
                <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
                  <Loader2 className="w-4 h-4 animate-spin text-primary" />
                  Carregando motivos de OS...
                </div>
              ) : (
                <select
                  value={selectedMotivo}
                  onChange={(e) => setSelectedMotivo(e.target.value)}
                  className="w-full h-10 px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  required
                  data-testid="os-motivo-select"
                >
                  <option value="">-- Selecione o Motivo da OS --</option>
                  {motivosList.map((item, idx) => {
                    const id = item.id !== undefined ? item.id : (item.codigo !== undefined ? item.codigo : idx);
                    const label = item.descricao || item.nome || item.label || item.motivo || `Motivo ${id}`;
                    return (
                      <option key={idx} value={id}>
                        {label} (Código: {id})
                      </option>
                    );
                  })}
                </select>
              )}
            </div>

            {/* Conteúdo */}
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">
                Conteúdo da Ocorrência e OS <span className="text-red-500">*</span>
              </label>
              <Textarea
                placeholder="Descreva detalhadamente o conteúdo..."
                value={osConteudo}
                onChange={(e) => setOsConteudo(e.target.value)}
                rows={3}
                required
                className="bg-slate-50 text-sm"
                data-testid="os-conteudo-input"
              />
            </div>

            {/* Local / Bairro */}
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">
                Local (Bairro do cliente)
              </label>
              <Input
                placeholder="Informe o bairro do cliente"
                value={osBairro}
                onChange={(e) => setOsBairro(e.target.value)}
                className="bg-slate-50 text-sm"
                data-testid="os-bairro-input"
              />
            </div>

            {/* Observação */}
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">
                Observação
              </label>
              <Input
                placeholder="Observação da ocorrência (opcional)"
                value={osObservacao}
                onChange={(e) => setOsObservacao(e.target.value)}
                className="bg-slate-50 text-sm"
                data-testid="os-observacao-input"
              />
            </div>

            {/* Data e Hora de Agendamento */}
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">
                Data e Hora de Agendamento
              </label>
              <Input
                type="datetime-local"
                value={osAgendamento}
                onChange={(e) => setOsAgendamento(e.target.value)}
                className="bg-slate-50 text-sm"
                data-testid="os-agendamento-input"
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-3 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsOSModalOpen(false)}
                disabled={submittingOS}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={submittingOS || loadingOSLists}
                className="bg-primary hover:bg-primary/90"
                data-testid="os-submit-btn"
              >
                {submittingOS ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Wrench className="w-4 h-4 mr-2" />
                    Abrir Ordem de Serviço
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3">
    <Icon size={14} className="text-muted-foreground" />
    <div className="flex-1">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-sm">{value}</p>
    </div>
  </div>
);

export default CustomerPanel;
